import moment from 'moment'
import { paAdminCsvName, paAdminDate, paAdminDownloadCsv, paAdminMoney } from './paAdminConstants'

const DATE_FORMAT = 'YYYY-MM-DD'
const EXPORT_MAX_ROWS = 5000

/**
 * Listing behaviour shared by the POP Authority admin tabs (design admin-authority.js): the
 * per-page select, search and date range apply on "Search", the filters apply at once, the
 * server sorts and paginates. A component sets `paAdmin: { action, sort, tab }` (Vuex action of
 * the listing, default sort field, tab name for the CSV file).
 */
export default {
  data () {
    const from = moment().subtract(31, 'days').format(DATE_FORMAT)
    const to = moment().format(DATE_FORMAT)
    return {
      loading: false,
      loadError: '',
      records: [],
      total: 0,
      totalPaid: 0,
      stats: {},
      managers: [],
      page: 1,
      perPage: 15,
      searchInput: '',
      search: '',
      fromInput: from,
      toInput: to,
      from,
      to,
      sortField: (this.$options.paAdmin && this.$options.paAdmin.sort) || '',
      sortOrder: 'desc',
      filters: { campaignManager: 'all' },
      requestId: 0,
    }
  },
  computed: {
    pageCount () {
      return Math.max(1, Math.ceil(this.total / this.perPage))
    },
    managerOptions () {
      return [{ key: 'all', name: 'All' }].concat(this.managers || [])
    },
  },
  created () {
    this.loadData()
  },
  methods: {
    money: paAdminMoney,
    dateOnly: paAdminDate,
    buildParams (overrides = {}) {
      const values = {
        page: this.page,
        perPage: this.perPage,
        sortField: this.sortField,
        sortOrder: this.sortOrder,
        filterText: this.search,
        ...this.filters,
        ...overrides,
      }
      const params = new URLSearchParams()
      Object.keys(values).forEach((key) => {
        const value = values[key]
        if (value !== null && value !== undefined && value !== '') params.set(key, value)
      })
      if (this.from) params.set('startDate', moment(this.from, DATE_FORMAT).startOf('day').valueOf())
      if (this.to) params.set('endDate', moment(this.to, DATE_FORMAT).endOf('day').valueOf())
      return params.toString()
    },
    errorText (error, fallback) {
      return (error && error.response && error.response.data && error.response.data.msg) ||
        (error && error.paAdminMessage) || fallback
    },
    fetch (overrides) {
      return this.$store.dispatch(this.$options.paAdmin.action, { params: this.buildParams(overrides) })
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            const error = new Error('listing failed')
            error.paAdminMessage = data && data.msg
            throw error
          }
          return data
        })
    },
    loadData () {
      const id = ++this.requestId
      this.loading = true
      this.loadError = ''
      return this.fetch()
        .then((data) => {
          if (id !== this.requestId) return
          this.records = data.records || []
          this.total = data.total || 0
          this.totalPaid = data.totalPaid || 0
          this.stats = data.stats || {}
          this.managers = data.managers || []
          this.afterLoad(data)
        })
        .catch((error) => {
          if (id !== this.requestId) return
          this.records = []
          this.total = 0
          this.loadError = this.errorText(error, 'We could not load this list. Try again in a moment.')
        })
        .finally(() => {
          if (id === this.requestId) this.loading = false
        })
    },
    /** Hook for a tab that needs more than the list (content type columns...). */
    afterLoad () {},
    applySearch () {
      this.search = (this.searchInput || '').trim()
      this.from = this.fromInput
      this.to = this.toInput
      this.page = 1
      return this.loadData()
    },
    setPerPage (value) {
      this.perPage = parseInt(value, 10) || 15
      this.page = 1
      return this.loadData()
    },
    setPage (value) {
      const page = Math.min(Math.max(parseInt(value, 10) || 1, 1), this.pageCount)
      if (page === this.page) return null
      this.page = page
      return this.loadData()
    },
    setFilter (key, value) {
      this.$set(this.filters, key, value)
      this.page = 1
      return this.loadData()
    },
    sortBy (field) {
      if (this.sortField === field) {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortField = field
        this.sortOrder = 'desc'
      }
      this.page = 1
      return this.loadData()
    },
    sortMark (field) {
      if (this.sortField !== field) return ''
      return this.sortOrder === 'desc' ? '↓ ' : '↑ '
    },
    thClass (field, extra) {
      return ['paa-sortable', extra || '', this.sortField === field ? 'paa-sorted' : '']
    },
    /** Every row of the active search and filters (the export matches the listing). */
    fetchAll () {
      const perPage = Math.min(Math.max(this.total, 1), EXPORT_MAX_ROWS)
      return this.fetch({ page: 1, perPage }).then((data) => data.records || [])
    },
    exportCsv (header, toRow) {
      this.$store.commit('showLoading')
      return this.fetchAll()
        .then((records) => {
          paAdminDownloadCsv(paAdminCsvName(this.$options.paAdmin.tab), [header].concat(records.map(toRow)))
        })
        .catch((error) => {
          this.notifyError(this.errorText(error, 'The export failed. Try again in a moment.'))
        })
        .finally(() => {
          this.$store.commit('hideLoading')
        })
    },
    notifyError (text) {
      this.$notify({ group: 'info', type: 'error', text })
    },
    /** Admin > User Accounts with the customer already searched (new tab). */
    userAccountsHref (email) {
      const me = this.$store.state.user || {}
      return this.$router.resolve({
        path: `/admin/${me.id}`,
        query: { activeTab: 'userAccounts', search: email },
      }).href
    },
  },
}
